
import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { X, Lightbulb } from "lucide-react";
import { cn } from "@/lib/utils";

interface SmartHintProps {
  id: string;
  title: string;
  description: string;
  position?: "top" | "bottom" | "left" | "right";
  className?: string;
}

export function SmartHint({ 
  id, 
  title, 
  description, 
  position = "top",
  className 
}: SmartHintProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [hasBeenShown, setHasBeenShown] = useState(false);
  const [adjustedPosition, setAdjustedPosition] = useState(position);
  const hintRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const shown = localStorage.getItem(`smart-hint-${id}`);
    if (!shown) {
      const timer = setTimeout(() => {
        setIsVisible(true);
        setHasBeenShown(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [id]);

  useEffect(() => {
    if (isVisible && hintRef.current && triggerRef.current) {
      const hint = hintRef.current;
      const rect = hint.getBoundingClientRect();
      const viewport = {
        width: window.innerWidth,
        height: window.innerHeight
      };

      // Check if hint goes off screen and adjust position
      let newPosition = position;
      
      if (position === "right" && rect.right > viewport.width - 20) {
        newPosition = "left";
      } else if (position === "left" && rect.left < 20) {
        newPosition = "right";
      } else if (position === "top" && rect.top < 20) {
        newPosition = "bottom";
      } else if (position === "bottom" && rect.bottom > viewport.height - 20) {
        newPosition = "top";
      }

      setAdjustedPosition(newPosition);
    }
  }, [isVisible, position]);

  const handleDismiss = () => {
    setIsVisible(false);
    localStorage.setItem(`smart-hint-${id}`, "shown");
  };

  const getPositionClasses = () => {
    switch (adjustedPosition) {
      case "top":
        return "bottom-full mb-2 left-1/2 transform -translate-x-1/2";
      case "bottom":
        return "top-full mt-2 left-1/2 transform -translate-x-1/2";
      case "left":
        return "right-full mr-2 top-1/2 transform -translate-y-1/2";
      case "right":
        return "left-full ml-2 top-1/2 transform -translate-y-1/2";
      default:
        return "bottom-full mb-2 left-1/2 transform -translate-x-1/2";
    }
  };

  const getArrowClasses = () => {
    switch (adjustedPosition) {
      case "top":
        return "top-full left-1/2 transform -translate-x-1/2 border-t-yellow-400 border-l-transparent border-r-transparent border-b-transparent";
      case "bottom":
        return "bottom-full left-1/2 transform -translate-x-1/2 border-b-yellow-400 border-l-transparent border-r-transparent border-t-transparent";
      case "left":
        return "left-full top-1/2 transform -translate-y-1/2 border-l-yellow-400 border-t-transparent border-b-transparent border-r-transparent";
      case "right":
        return "right-full top-1/2 transform -translate-y-1/2 border-r-yellow-400 border-t-transparent border-b-transparent border-l-transparent";
      default:
        return "top-full left-1/2 transform -translate-x-1/2 border-t-yellow-400 border-l-transparent border-r-transparent border-b-transparent";
    }
  };

  if (!hasBeenShown) return null;

  return (
    <div ref={triggerRef} className={cn("relative inline-block", className)}>
      <Button
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0 text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50"
        onClick={() => setIsVisible(!isVisible)}
      >
        <Lightbulb className="h-4 w-4" />
      </Button>

      {isVisible && (
        <div
          ref={hintRef}
          className={cn(
            "absolute z-50 w-80 max-w-sm",
            getPositionClasses()
          )}
          style={{
            maxWidth: 'min(320px, calc(100vw - 40px))'
          }}
        >
          <Card className="bg-gradient-to-r from-yellow-400 to-amber-500 border-0 shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Lightbulb className="h-5 w-5 text-white mt-0.5 flex-shrink-0" />
                <div className="flex-1 text-white">
                  <h4 className="font-semibold text-sm mb-1">{title}</h4>
                  <p className="text-xs opacity-90 leading-relaxed">{description}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 text-white hover:bg-white/20 flex-shrink-0"
                  onClick={handleDismiss}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            </CardContent>
          </Card>
          
          {/* Arrow */}
          <div
            className={cn("absolute w-0 h-0 border-4", getArrowClasses())}
          />
        </div>
      )}
    </div>
  );
}
